import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import { LOCALES_DEBUG } from '@lib/enums/localStorageKeys';
import resources from './resources';
import { getBrowserLanguages, resolveLanguage } from './detectLanguage';

const { VITE_ENVIRONMENT } = import.meta.env;

// Check if debug mode is enabled
const isDebugMode = () => {
  if (typeof window === 'undefined' || !('localStorage' in window))
    return false;

  try {
    const debug = window.localStorage.getItem(LOCALES_DEBUG);
    return debug ? JSON.parse(debug) : false;
  } catch (error) {
    console.warn('✋', error);
    // Access to localStorage may throw in opaque origins (e.g., headless converters)
    return false;
  }
};

const supportedLanguages = Object.keys(resources);
const fallbackLanguage = 'en';

// Default language mirrors the backend: French in production, English
// everywhere else.
const preferredLanguage = VITE_ENVIRONMENT === 'PROD' ? 'fr' : 'en';

function detectBrowserLanguage(): string {
  // Preferred-first resolution: the preferred language is served whenever the
  // user understands it, otherwise the first supported preference, otherwise
  // the preferred language.
  return resolveLanguage(
    getBrowserLanguages(),
    supportedLanguages,
    preferredLanguage,
  );
}

i18n
  .use(initReactI18next)
  .use(ICU)
  .init({
    resources,
    lng: detectBrowserLanguage(),
    fallbackLng: fallbackLanguage,
    interpolation: {
      escapeValue: false,
    },
    ...(isDebugMode() && {
      // In debug mode, return the key instead of the translation
      parseMissingKeyHandler: (key: string) => key,
      returnEmptyString: false,
      returnNull: false,
      // Override the translation function to return keys
      missingKeyHandler: () => undefined,
    }),
  });

// Override the t function in debug mode
if (isDebugMode()) {
  const originalT = i18n.t.bind(i18n);
  i18n.t = function debugT(key: any, options?: any) {
    if (typeof key === 'string') {
      return key;
    }
    return originalT(key, options);
  } as typeof i18n.t;
}

export default i18n;
